import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, allowancesQuestionsData, exemptionQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function OregonForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formatFullName,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_name = formatFullName();

  // step 1 questions
  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  // for the is_exempt question
  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const allowancesForm = useRef();
  const allowancesInitialQuestions = getInitialQuestions(allowancesQuestionsData);
  const [allowancesQuestions, setAllowancesQuestions] = useState(allowancesInitialQuestions);

  // exemption questions
  const exemptionsForm = useRef();
  const exemptionsInitialQuestions = getInitialQuestions(exemptionQuestions);
  const [exemptionsQuestions, setExemptionsQuestions] = useState(exemptionsInitialQuestions);

  // no calculations here

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding. (Line 4)
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderAllowanceQuestions = (isReviewStep) => (
    <FormConsumer
      questions={allowancesQuestions}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = allowancesQuestions.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setAllowancesQuestions(questions);
      }}
      ref={allowancesForm}
    />
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <FormConsumer
      questions={exemptionsQuestions}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = exemptionsQuestions.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setExemptionsQuestions(questions);
      }}
      ref={exemptionsForm}
    />
  );

  const handleValidation = () => {
    // Basic info validation
    if (step1Form && step1Form.current
      && step1Form.current.validate((error) => !!error)) {
      return true;
    }

    if (!isExemptFromWithholding) {
      // Check for allowances form if not exempt
      if (allowancesForm && allowancesForm.current
        && allowancesForm.current.validate((error) => !!error)) {
        return true;
      }
      // Check for exemptions form if exempt
    } else if (exemptionsForm && exemptionsForm.current
        && exemptionsForm.current.validate((error) => !!error)) {
      return true;
    }

    // No errors
    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();

    if (hasErrors) return;

    const stepOneData = stepOneQuestions
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    let allowancesData = resetInitialQuestions(allowancesQuestions)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let exemptionData = resetInitialQuestions(exemptionsQuestions)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    if (!isExemptFromWithholding) {
      allowancesData = allowancesQuestions
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    } else {
      exemptionData = exemptionsQuestions
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    }

    const payload = {
      ...stepOneData,
      ...allowancesData,
      ...exemptionData,
      is_exempt: isExemptFromWithholding,
    };

    saveFormData(payload, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {isExemptFromWithholding && renderExemptionsFromWithholding()}
          {!isExemptFromWithholding && renderAllowanceQuestions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {isExemptFromWithholding && renderExemptionsFromWithholding(true)}
        {!isExemptFromWithholding && renderAllowanceQuestions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default OregonForm;
